/** SUNTRONIX ONE — Brand Emphasis Profiles.
 *  Per-brand control of WHICH Smart Format categories get bolded. Backed by
 *  /api/descriptions/emphasis-profiles. Embeddable in Product Master (dialog)
 *  and ONE STUDIO (inline tab). Admin only. */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Save, RotateCcw, Loader2, Check, SlidersHorizontal } from "lucide-react";

export default function EmphasisProfiles({ compact = false }) {
  const [cats, setCats] = useState([]);
  const [rows, setRows] = useState([]);         // editable copy
  const [dirty, setDirty] = useState({});       // brand -> bool
  const [saving, setSaving] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/descriptions/emphasis-profiles");
      setCats(data.categories || []);
      setRows(data.profiles || []);
      setDirty({});
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load profiles"); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (brand, key) => {
    setRows((rs) => rs.map((r) => {
      if (r.brand !== brand) return r;
      const has = r.enabled.includes(key);
      return { ...r, enabled: has ? r.enabled.filter((c) => c !== key) : [...r.enabled, key] };
    }));
    setDirty((d) => ({ ...d, [brand]: true }));
  };

  const save = async (r) => {
    setSaving(r.brand);
    try {
      await api.put(`/descriptions/emphasis-profiles/${encodeURIComponent(r.key)}`, { enabled: r.enabled });
      toast.success(`${r.brand} emphasis saved`);
      setRows((rs) => rs.map((x) => x.brand === r.brand ? { ...x, source: "custom" } : x));
      setDirty((d) => ({ ...d, [r.brand]: false }));
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving("");
  };

  const reset = async (r) => {
    setSaving(r.brand);
    try {
      const { data } = await api.delete(`/descriptions/emphasis-profiles/${encodeURIComponent(r.key)}`);
      setRows((rs) => rs.map((x) => x.brand === r.brand ? { ...x, enabled: data.enabled, source: "recommended" } : x));
      setDirty((d) => ({ ...d, [r.brand]: false }));
      toast.success(`${r.brand} reset to recommended`);
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
    setSaving("");
  };

  if (loading) return <div className="py-10 text-center text-slate-400 text-[13px]"><Loader2 className="h-5 w-5 mx-auto animate-spin mb-2" />Loading emphasis profiles…</div>;

  return (
    <div className="space-y-3" data-testid="emphasis-profiles">
      <p className="text-[12px] text-slate-500">
        Choose which spec types Smart Format bolds for each brand. Products with no brand use <b>Global default</b>.
        These rules apply to the ✨ Smart Format button and the bulk Smart Format run.
      </p>
      <div className={`space-y-2 ${compact ? "max-h-[52vh] overflow-y-auto pr-1" : ""}`}>
        {rows.map((r) => (
          <div key={r.brand} className="border border-slate-200 rounded-lg p-3 bg-white" data-testid={`emphasis-row-${r.key}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[13px] font-semibold text-slate-900">{r.brand}</span>
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full border ${r.source === "custom" ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>
                {r.source === "custom" ? "Custom" : "Recommended"}
              </span>
              <span className="ml-auto flex items-center gap-1.5">
                {r.source === "custom" && (
                  <button onClick={() => reset(r)} disabled={saving === r.brand}
                    data-testid={`emphasis-reset-${r.key}`}
                    className="text-[11px] text-slate-500 hover:text-slate-800 inline-flex items-center gap-1">
                    <RotateCcw className="h-3 w-3" /> Reset
                  </button>
                )}
                <Button size="sm" onClick={() => save(r)} disabled={saving === r.brand || !dirty[r.brand]}
                  data-testid={`emphasis-save-${r.key}`}
                  className="h-7 bg-slate-900 hover:bg-slate-800 disabled:opacity-40">
                  {saving === r.brand ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />} Save
                </Button>
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {cats.map((c) => {
                const on = r.enabled.includes(c.key);
                return (
                  <button key={c.key} onClick={() => toggle(r.brand, c.key)}
                    data-testid={`emphasis-${r.key}-${c.key}`}
                    className={"text-[11px] px-2 py-1 rounded-full border inline-flex items-center gap-1 transition-colors " +
                      (on ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-white text-slate-400 border-slate-200 hover:border-slate-300")}>
                    {on && <Check className="h-3 w-3" />}{c.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Self-gating button + dialog for embedding in Product Master (hidden for non-admin). */
export function EmphasisProfilesButton() {
  const [allowed, setAllowed] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    api.get("/descriptions/emphasis-profiles")
      .then(() => setAllowed(true)).catch(() => setAllowed(false));
  }, []);
  if (!allowed) return null;
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="emphasis-profiles-open"
        className="border border-slate-200 rounded-xl bg-white px-3 py-2.5 text-left hover:border-indigo-300 transition-colors min-w-[190px]">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-700">
          <SlidersHorizontal className="h-3.5 w-3.5 text-indigo-600" /> Brand Emphasis
        </div>
        <div className="mt-1 text-[11px] text-slate-400">Which specs get bolded, per brand</div>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="emphasis-profiles-dialog">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <SlidersHorizontal className="h-4 w-4 text-indigo-600" /> Brand Emphasis Profiles
            </DialogTitle>
            <DialogDescription className="text-[12px]">
              Tune which spec types Smart Format emphasises for each brand.
            </DialogDescription>
          </DialogHeader>
          <EmphasisProfiles compact />
        </DialogContent>
      </Dialog>
    </>
  );
}
